<template>
  <div class="form-group-wrapper">
    <form @submit.prevent="saveGroup">
      <!-- Name -->
      <div
        class="form-group"
        :class="{ invalid: errors.name, hasValue: groupData.name.length }"
      >
        <span class="label">{{ errors.name || label.name }}</span>
        <b-form-input
          :disabled="!isAdmin"
          required
          id="nameInput"
          v-model="groupData.name"
          :placeholder="label.name"
        >
        </b-form-input>
      </div>
      <!-- END - Name -->

      <!-- Managers -->
      <div
        class="form-group"
        :class="{
          invalid: errors.managers,
          hasValue: groupData.managers.length,
        }"
        @keydown.enter.prevent=""
        @keypress.enter.prevent=""
        @keyup.enter.prevent=""
      >
        <span class="label">{{ errors.managers || label.managers }}</span>
        <v-select
          :disabled="!isAdmin"
          multiple
          id="managersSelect"
          v-model="groupData.managers"
          :options="optionManagers"
          ref="managers"
          :filterable="false"
          :closeOnSelect="true"
          @keydown.enter.prevent=""
          @search="searchForManagers"
        >
          <template slot="option" slot-scope="option">
            <div>
              {{ `${option.profile.firstname} ${option.profile.lastname}` }}
              <br />
              <small>{{ removeFakeEmail(option.email) }}</small>
            </div>
          </template>

          <template slot="no-options">
            {{ label.search }}
          </template>

          <template slot="selected-option" slot-scope="option">
            <div class="tag-wrap" :title="option.email">
              <span class="tag-image mdi mdi-account"></span>
              <span class="tag-label">
                {{ `${option.profile.firstname} ${option.profile.lastname}` }}
              </span>
            </div>
          </template>
        </v-select>
      </div>
      <!-- END - Managers -->

      <!-- Members -->
      <div
        class="form-group"
        :class="{ invalid: errors.members, hasValue: groupData.members.length }"
        @keydown.enter.prevent=""
        @keypress.enter.prevent=""
        @keyup.enter.prevent=""
      >
        <span class="label">{{ errors.members || label.members }}</span>
        <v-select
          :disabled="!isAdmin"
          multiple
          id="membersSelect"
          v-model="groupData.members"
          :options="optionMembers"
          :filterable="false"
          :closeOnSelect="true"
          @search="searchForMembers"
        >
          <template slot="option" slot-scope="option">
            <div>
              {{ `${option.profile.firstname} ${option.profile.lastname}` }}
              <br />
              <small>{{ removeFakeEmail(option.email) }}</small>
            </div>
          </template>

          <template slot="no-options">
            {{ label.search }}
          </template>

          <template slot="selected-option" slot-scope="option">
            <div
              :id="
                `idMember${option.profile.firstname}${option.profile.lastname}`
              "
              class="tag-wrap"
              :title="option.email"
            >
              <span class="tag-image mdi mdi-account"></span>
              <span class="tag-label">
                {{ `${option.profile.firstname} ${option.profile.lastname}` }}
              </span>
            </div>
          </template>
        </v-select>
      </div>
      <!-- END - Members -->

      <!-- Subgroups -->
      <div
        class="form-group"
        v-if="group._id"
        :class="{
          invalid: errors.children,
          hasValue: groupData.children.length,
        }"
        @keydown.enter.prevent=""
        @keypress.enter.prevent=""
        @keyup.enter.prevent=""
      >
        <span class="label">{{ errors.children || label.subgroups }}</span>
        <v-select
          :disabled="!isAdmin"
          multiple
          id="subGroupsSelect"
          v-model="groupData.children"
          :options="optionGroups"
          :label="'name'"
          @search="searchForGroups"
        >
          <template slot="option" slot-scope="option">
            {{ option.name }}
          </template>

          <template slot="no-options">
            {{ label.search }}
          </template>

          <template slot="selected-option" slot-scope="option">
            <div class="tag-wrap">
              <span class="tag-image mdi mdi-account"></span>
              <span class="tag-label">{{ option.name }}</span>
            </div>
          </template>
        </v-select>
      </div>
      <!-- END - Subgroups -->

      <div
        class="form-group group-inactive-switch"
        v-if="$can('action', 'inactive.group')"
      >
        <Switches
          v-model="groupData.active"
          type-bold="true"
          theme="custom"
          color="primary"
        />
        <span class="group-inactive-label" v-if="!groupData.active">
          {{ label.inactive }}
          <span class="group-inactive-description">
            {{ label.inactiveDescription }}
          </span>
        </span>
        <span class="group-inactive-label" v-else>
          {{ label.active }}
          <span class="group-inactive-description">
            {{ label.activeDescription }}
          </span>
        </span>
      </div>

      <footer class="text-right">
        <b-button
          class="btn-create"
          variant="primary"
          type="submit"
          :disabled="submitInProgress"
        >
          <i
            class="mdi mdi-spin pr-2"
            :class="{ 'mdi-loading': submitInProgress }"
          >
          </i>
          {{ label.save }}
        </b-button>
      </footer>
    </form>
    <b-modal
      id="modalActiveGroup"
      :title="label.active"
      :cancel-title="label.activeGroupModal.cancel"
      :ok-title="label.activeGroupModal.ok"
      @cancel="groupData.active = false"
      cancel-variant="outline-primary"
      ref="modalActiveGroup"
      header-bg-variant="light"
    >
      <p class="txt-20">{{ label.activeGroupModal.text1 }}</p>
      <p class="txt-20">{{ label.activeGroupModal.text2 }}</p>
      <p class="txt-20">{{ label.activeGroupModal.text3 }}</p>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { searchUsers } from '@/service/users'
import {
  searchGroups,
  validateManagers,
  validateMembers,
  validateChildren,
} from '@/service/groups'
import { getUser } from '@/service/user'
import { removeFakeEmail } from '@/helpers/mailFake'
import Switches from 'vue-switches'

const props = {
  group: {
    type: Object,
    required: false,
  },
  groupErrors: {
    type: Object,
    required: false,
  },
  submitInProgress: {
    type: Boolean,
    required: false,
    default: false,
  },
}

let timeoutDebounceID

function debounce(fn, delay) {
  return (() => {
    clearTimeout(timeoutDebounceID)
    timeoutDebounceID = setTimeout(() => fn.apply(this, arguments), delay)
  })()
}

export default {
  props: props,
  components: {
    vSelect: () => import('vue-select'),
    Switches,
  },
  data: function() {
    return {
      optionForManagers: [],
      optionForMembers: [],
      optionForGroups: [],
      groupData: {
        name: '',
        managers: [],
        members: [],
        children: [],
        active: true, // @TODO mudar para false
      },
      errors: {
        name: false,
        managers: false,
        members: false,
        children: false,
      },
    }
  },

  watch: {
    'groupData.active': function(value) {
      if (value) {
        this.$refs.modalActiveGroup.show()
      }
    },

    group: function() {
      this.group.active = !this.group.inactive
      this.groupData = JSON.parse(JSON.stringify(this.group))
    },

    groupErrors: function(errors) {
      this.validateErrors(errors)
    },

    'groupData.name': function() {
      this.errors.name = false
    },

    'groupData.children': function(value) {
      this.errors.children = false

      if (value.length) {
        validateChildren(this.getGroup()).catch(err => {
          try {
            this.errors.children = err.response.data.children.message
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e)
            this.errors.children = 'erro inesperado'
          }
        })
      }
    },

    'groupData.members': function(value) {
      this.errors.managers = false
      this.errors.members = false

      if (value.length) {
        validateMembers(this.getGroup()).catch(err =>
          this.validateErrors(err.response.data)
        )
      }
    },

    'groupData.managers': function(value) {
      this.errors.managers = false
      this.errors.members = false

      if (value.length) {
        validateManagers(this.getGroup()).catch(err =>
          this.validateErrors(err.response.data)
        )
      }
    },
  },

  computed: {
    ...mapGetters({
      resources: 'resources',
      modal: 'groups/modal',
    }),

    label: function() {
      return this.resources.page.home.groups.form
    },

    optionManagers: function() {
      return this.normalizeUserLabel(this.optionForManagers)
    },

    optionMembers: function() {
      return this.normalizeUserLabel(this.optionForMembers)
    },

    optionGroups: function() {
      return this.optionForGroups.filter(item => {
        return this.groupData._id !== item._id
      })
    },

    isAdmin() {
      return getUser().role === 'admin'
    },
  },

  methods: {
    removeFakeEmail,

    getGroup() {
      return {
        id: this.groupData._id,
        name: this.groupData.name,
        managers: this.groupData.managers.map(item =>
          item.id ? item.id : item._id
        ),
        members: this.groupData.members.map(item =>
          item.id ? item.id : item._id
        ),
        children: this.groupData.children.map(item =>
          item.id ? item.id : item._id
        ),
        inactive: !this.groupData.active,
      }
    },

    validateErrors(errors) {
      Object.keys(errors).forEach(key => {
        this.errors[key] = errors[key] ? errors[key].message : ''
      })
    },

    saveGroup() {
      this.$emit('saveGroup', this.getGroup())
    },

    searchForManagers(search, loading) {
      const onSuccess = res => (this.optionForManagers = res.users)

      this.searchForUsers(search, loading, onSuccess)
    },

    searchForMembers(search, loading) {
      const onSuccess = res => (this.optionForMembers = res.users)

      this.searchForUsers(search, loading, onSuccess)
    },

    searchForUsers(search, loading, onSuccess) {
      loading(true)

      debounce(() => {
        searchUsers({ name: search, limit: 5, status: 'activated' })
          .then(onSuccess)
          .then(() => loading(false))
          .catch(this.onFindUsersError)
          .then(() => loading(false))
      }, 300)
    },

    searchForGroups(search, loading) {
      loading(true)
      debounce(() => {
        searchGroups({ name: search, limit: 5 })
          .then(groups => (this.optionForGroups = groups))
          .then(() => loading(false))
          .catch(this.onFindUsersError)
          .then(() => loading(false))
      }, 300)
    },

    normalizeUserLabel(users) {
      return users.map(item =>
        Object.assign(item, {
          label: `${item.id || item._id}`,
        })
      )
    },

    onFindUsersError(error) {
      // eslint-disable-next-line no-console
      console.error('error', error)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/styles/custom/colors';

.form-group {
  margin-top: 0rem !important;

  .label {
    margin-left: 20px;
    color: #32132e;
    font-size: 14px;

    &.opacity {
      opacity: 0 !important;
    }
  }

  &.invalid {
    .label {
      color: map-get($theme-colors, 'red');
    }

    .v-select {
      .vs__dropdown-toggle {
        border: 1px solid map-get($theme-colors, 'red');
      }
    }
  }

  &.hasValue {
    .vs__dropdown-menu,
    .vs__dropdown-toggle,
    .form-control {
      background: map-get($theme-colors, 'gray07') !important;
      background: map-get($theme-colors, 'gray07') !important;
    }
  }
}

.form-group-wrapper {
  .form-control {
    background: none !important;
    border-color: none !important;

    &::-webkit-input-placeholder {
      font-size: small;
    }
    &:-moz-placeholder {
      font-size: small;
    }
    &::-moz-placeholder {
      font-size: small;
    }
    &:-ms-input-placeholder {
      font-size: small;
    }
    &::placeholder {
      font-size: small;
    }
  }
}

.btn-create {
  min-width: 150px;
}

footer {
  margin-top: 15px;
}

.group-inactive-switch {
  margin-top: 2rem !important;
  color: #32132e;
  font-size: 14px;

  .group-inactive-label {
    margin-left: 0.5rem !important;
  }

  .vue-switcher {
    margin-bottom: -1rem !important;
  }

  .group-inactive-description {
    font-size: 12px !important;
    color: map-get($theme-colors, 'gray03');
  }
}
</style>
